import { memo, useMemo } from 'react';

import { CircleXIcon, EllipsisIcon, FolderUpIcon, UploadIcon } from '@air/design-icons';
import { DropdownMenu, type DropdownMenuOption, renderDropdownItems } from '@air/primitive-dropdown-menu';
import { IconButton } from '@air/primitive-icon-button';

export type WorkspaceUploadsHeaderMenuProps = {
  onCancelAllUploads: (() => void) | undefined;
  onClearRecentUploads: (() => void) | undefined;
  onUploadFiles: () => void;
  onUploadFolders: (() => void) | undefined;
};

export const WorkspaceUploadsHeaderMenu = memo(
  ({ onCancelAllUploads, onClearRecentUploads, onUploadFiles, onUploadFolders }: WorkspaceUploadsHeaderMenuProps) => {
    const options = useMemo(() => {
      const menuOptions: DropdownMenuOption[] = [
        {
          id: 'upload-files',
          label: 'File upload',
          onSelect: onUploadFiles,
          prefix: <UploadIcon aria-hidden size="sm" />,
          type: 'item',
        },
      ];

      if (onUploadFolders) {
        menuOptions.push({
          id: 'upload-folders',
          label: 'Folder upload',
          onSelect: onUploadFolders,
          prefix: <FolderUpIcon aria-hidden size="sm" />,
          type: 'item',
        });
      }

      if (onClearRecentUploads || onCancelAllUploads) {
        menuOptions.push({
          id: 'upload-actions-separator',
          type: 'separator',
        });
      }

      if (onClearRecentUploads) {
        menuOptions.push({
          id: 'clear-recent-uploads',
          label: 'Clear recent uploads',
          onSelect: onClearRecentUploads,
          prefix: <CircleXIcon aria-hidden size="sm" />,
          type: 'item',
        });
      }

      if (onCancelAllUploads) {
        menuOptions.push({
          id: 'cancel-all-uploads',
          label: 'Cancel all uploads',
          onSelect: onCancelAllUploads,
          prefix: <CircleXIcon aria-hidden size="sm" />,
          type: 'item',
        });
      }

      return menuOptions;
    }, [onCancelAllUploads, onClearRecentUploads, onUploadFiles, onUploadFolders]);

    return (
      <DropdownMenu
        trigger={
          <IconButton
            adsIcon
            appearance="ghost"
            color="gray"
            data-testid="WORKSPACE_UPLOADS_OVERFLOW_BUTTON"
            icon={EllipsisIcon}
            label="Uploads menu"
            size="small"
          />
        }
      >
        {renderDropdownItems({ options })}
      </DropdownMenu>
    );
  },
);

WorkspaceUploadsHeaderMenu.displayName = 'WorkspaceUploadsHeaderMenu';
